import { Injectable, ViewContainerRef, ComponentFactoryResolver, ComponentRef } from '@angular/core';
import { DialogBase, DialogCloseReason, IDialogOnClose } from './base-dialog';
import { AlertDialogComponent, IAlertData } from './alert/alert.component';
import { IConfirmData, ConfirmDialogComponent } from './confirm/confirm.component';
import { Subject } from 'rxjs';
import { DialogBackComponent } from './dialog-back.compnent';
import { IInputData, InputDialogComponent } from './input/input.component';

export interface IDialogHandle {
  onClose: Subject<IDialogOnClose>;
  close: (reason?: IDialogOnClose) => void;
}

export interface IDialogOptions {
  closeOnEscape?: boolean;
  showCancelOnHeader?: boolean;
  backdropClass?: string; //a class that will be added to this dialog instance's backdrop
  closeWhenClickedOnBackdrop?: boolean;
}

export interface IConstructor<T> {
  new(...args: any[]): T;
}


@Injectable({ providedIn: 'root' })
export class DialogService {

  private viewContainer: ViewContainerRef;

  private openDialogs: Array<DialogBase> = [];

  constructor(private componentFactoryResolver: ComponentFactoryResolver) {
    document.addEventListener('keydown', this.handleKeyboardEvent);
  }

  private handleKeyboardEvent = (event: KeyboardEvent): void => {
    if (this.openDialogs.length > 0) {
      event.stopPropagation();
      const lastDialog = this.openDialogs[this.openDialogs.length - 1];
      if (event.keyCode === 27 && lastDialog.dialogOptions && lastDialog.dialogOptions.closeOnEscape) {
        lastDialog.cancel();
      }
    }
  }

  public setViewContainer = (viewContainer: ViewContainerRef): void => {
    this.viewContainer = viewContainer;
  }

  public alert = (data: IAlertData, dialogOptions?: IDialogOptions): IDialogHandle => {
    return this.createDialog(AlertDialogComponent, Object.assign({}, data), dialogOptions);
  }

  public confirm = (data: IConfirmData, dialogOptions?: IDialogOptions): IDialogHandle => {
    return this.createDialog(ConfirmDialogComponent, Object.assign({}, data), dialogOptions);
  }

  public input = (data: IInputData, dialogOptions?: IDialogOptions): IDialogHandle => {
    return this.createDialog(InputDialogComponent, Object.assign({}, data), dialogOptions);
  }

  public createDialog = <d extends DialogBase>(dialogComponent: IConstructor<d>, extraData?: any, dialogOptions?: IDialogOptions): IDialogHandle => {
    const effectingDialogOptions: IDialogOptions = {
      closeOnEscape: true,
      showCancelOnHeader: true,
      backdropClass: null,
      closeWhenClickedOnBackdrop: false
    };

    if (dialogOptions) {
      Object.assign(effectingDialogOptions, dialogOptions);
    }

    const backdropComponentFactory = this.componentFactoryResolver.resolveComponentFactory(DialogBackComponent);
    const _currentBackdropRef = this.viewContainer.createComponent(backdropComponentFactory);

    if (effectingDialogOptions.backdropClass) {
      _currentBackdropRef.location.nativeElement.classList.add(effectingDialogOptions.backdropClass);
    }

    const dialogComponentFactory = this.componentFactoryResolver.resolveComponentFactory(dialogComponent);
    const _currentDialogRef = this.viewContainer.createComponent(dialogComponentFactory);
    _currentDialogRef.location.nativeElement.classList.add('dialog-instance');
    _currentDialogRef.instance.onClose.subscribe(() => this.onDialogClose(_currentDialogRef, _currentBackdropRef));

    if (effectingDialogOptions.closeWhenClickedOnBackdrop) {
      _currentDialogRef.location.nativeElement.addEventListener('click', (event: any) => {
        if (event.target === _currentDialogRef.location.nativeElement) {
          // only if it the container itself of the dialog was clicked that means that it was clicked outside of the main dialog
          // then we close the dialog.
          this.onDialogClose(_currentDialogRef, _currentBackdropRef);
        }
      });
    }

    _currentDialogRef.instance.extraData = extraData;
    _currentDialogRef.instance.dialogOptions = effectingDialogOptions;
    _currentDialogRef.instance.initDialog();
    this.openDialogs.push(_currentDialogRef.instance);

    return <IDialogHandle>{
      onClose: _currentDialogRef.instance.onClose,
      close: (reason?: IDialogOnClose) => {
        if (reason) {
          _currentDialogRef.instance.onClose.next(reason);
        } else {
          _currentDialogRef.instance.onClose.next({ reason: DialogCloseReason.ABORTED });
        }
      }
    };
  }

  private onDialogClose = (dialogRef: ComponentRef<DialogBase>, backdropRef: ComponentRef<DialogBackComponent>): void => {
    this.openDialogs = this.openDialogs.filter((d: DialogBase) => d !== dialogRef.instance);
    backdropRef.destroy();
    backdropRef = null;
    dialogRef.instance.onClose.unsubscribe();
    dialogRef.destroy();
    dialogRef = null;
  }

}
